import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider, useTenant } from "@/context/TenantContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import SuccessOverlay from "@/components/SuccessOverlay";
import { lazy, Suspense } from "react";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
// Cada página se descarga solo cuando se abre: antes todo iba en un solo
// main.js de 1.3 MB y un cliente que solo compra bajaba el panel admin entero.
// Si alguien tenía la pestaña abierta desde antes de un despliegue, su
// index.html apunta a archivos que ya no existen: se recarga una vez para
// traer la versión nueva en vez de caer en la pantalla de error.
const lazyPage = (load) => lazy(() => load().then((m) => {
  try { sessionStorage.removeItem("chunk-reload"); } catch {}
  return m;
}).catch((err) => {
  let yaRecargo = false;
  try { yaRecargo = sessionStorage.getItem("chunk-reload") === "1"; sessionStorage.setItem("chunk-reload", "1"); } catch {}
  if (!yaRecargo) { window.location.reload(); return new Promise(() => {}); }
  throw err;
}));
const Store = lazyPage(() => import("@/pages/Store"));
const Catalogo = lazyPage(() => import("@/pages/Catalogo"));
const Reportes = lazyPage(() => import("@/pages/Reportes"));
const Codigos = lazyPage(() => import("@/pages/Codigos"));
const Negocio = lazyPage(() => import("@/pages/Negocio"));
const AdminPanel = lazyPage(() => import("@/pages/AdminPanel"));
import LinkEmailGate from "@/components/LinkEmailGate";
import { DiaMuertosOverlay, DiaMuertosPie } from "@/components/DiaMuertosDecor";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const { tenant } = useTenant();
  if (loading) return <div className="min-h-screen grid place-items-center text-red-600 font-head text-xl">Cargando MQ Store...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Vincular un correo real es obligatorio -- sin esto no hay forma de
  // recuperar la cuenta si se olvida la contraseña. Bloquea cualquier ruta
  // protegida (tienda, reportes, admin) hasta que se cumpla -- pero solo si
  // ESTA tienda de verdad puede mandar el código (email_enabled, ver
  // /tenant/config): si no tiene Resend configurado, exigirlo dejaría a
  // todos sus clientes atrapados pidiendo un código que nunca les llega.
  if (!user.real_email && tenant?.email_enabled) return <LinkEmailGate />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <TenantProvider>
          <AuthProvider>
            <BrowserRouter>
              <Suspense fallback={<div className="min-h-screen grid place-items-center text-red-600 font-head text-xl">Cargando MQ Store...</div>}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={<Landing />} />
                  <Route path="/catalogo" element={<Catalogo />} />
                  <Route path="/compras" element={<Protected><Store /></Protected>} />
                  <Route path="/reportes" element={<Protected><Reportes /></Protected>} />
                  <Route path="/codigos" element={<Protected><Codigos /></Protected>} />
                  <Route path="/negocio" element={<Protected><Negocio /></Protected>} />
                  <Route path="/admin" element={<Protected role="admin"><AdminPanel /></Protected>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
              <DiaMuertosPie />
            </BrowserRouter>
            <Toaster position="bottom-left" richColors />
            <SuccessOverlay />
            <DiaMuertosOverlay />
          </AuthProvider>
        </TenantProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
